import { useState, useCallback, useRef } from "react";
import { Button, Card, CardContent } from "@learn-me/ui";
import {
  Upload,
  X,
  FileText,
  Image,
  Loader2,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import type { TempFile } from "../api/adminApi";
import { adminApi } from "../api/adminApi";
import toast from "react-hot-toast";

interface FileUploaderProps {
  onFilesChange: (files: TempFile[]) => void;
  files: TempFile[];
  maxFiles?: number;
  accept?: string;
  disabled?: boolean;
}

interface UploadingFile {
  id: string;
  name: string;
  progress: number;
  error?: string;
}

export function FileUploader({
  onFilesChange,
  files,
  maxFiles = 10,
  accept = ".pdf,.png,.jpg,.jpeg,.gif,.webp",
  disabled = false,
}: FileUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) setIsDragging(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const uploadFile = async (file: File): Promise<TempFile | null> => {
    const uploadId = `${Date.now()}-${file.name}`;

    setUploadingFiles((prev) => [
      ...prev,
      { id: uploadId, name: file.name, progress: 0 },
    ]);

    try {
      // Simulate progress (actual progress requires xhr or fetch with progress)
      setUploadingFiles((prev) =>
        prev.map((f) => (f.id === uploadId ? { ...f, progress: 30 } : f)),
      );

      const result = await adminApi.uploadFile(file);

      setUploadingFiles((prev) =>
        prev.map((f) => (f.id === uploadId ? { ...f, progress: 100 } : f)),
      );

      // Remove from uploading after brief delay to show completion
      setTimeout(() => {
        setUploadingFiles((prev) => prev.filter((f) => f.id !== uploadId));
      }, 500);

      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error al subir archivo";
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.id === uploadId ? { ...f, error: errorMessage, progress: 0 } : f,
        ),
      );
      toast.error(`Error al subir ${file.name}`);
      return null;
    }
  };

  const processFiles = async (fileList: FileList | File[]) => {
    const filesToUpload = Array.from(fileList);
    const remainingSlots = maxFiles - files.length;

    if (filesToUpload.length > remainingSlots) {
      toast.error(`Solo puedes subir ${remainingSlots} archivo(s) más`);
      return;
    }

    const uploadPromises = filesToUpload.map((file) => uploadFile(file));
    const results = await Promise.all(uploadPromises);
    const successfulUploads = results.filter((r): r is TempFile => r !== null);

    if (successfulUploads.length > 0) {
      onFilesChange([...files, ...successfulUploads]);
    }
  };

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      if (disabled) return;

      const droppedFiles = e.dataTransfer.files;
      await processFiles(droppedFiles);
    },
    [disabled, files, maxFiles, onFilesChange],
  );

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      await processFiles(e.target.files);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeFile = async (publicId: string) => {
    try {
      await adminApi.deleteUpload(publicId);
      onFilesChange(files.filter((f) => f.publicId !== publicId));
    } catch (error) {
      toast.error("Error al eliminar archivo");
    }
  };

  const removeUploadingFile = (id: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type: "PDF" | "IMAGE") => {
    return type === "PDF" ? (
      <FileText className="h-5 w-5 text-red-500" />
    ) : (
      <Image className="h-5 w-5 text-blue-500" />
    );
  };

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
          ${isDragging ? "border-primary-500 bg-primary-50" : "border-muted-foreground/25 hover:border-muted-foreground/50"}
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={handleFileSelect}
          disabled={disabled}
        />

        <Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
        <p className="text-sm font-medium mb-1">
          Arrastra archivos aquí o haz clic para seleccionar
        </p>
        <p className="text-xs text-muted-foreground">
          PDFs e imágenes (máx. {maxFiles} archivos)
        </p>
      </div>

      {/* Uploading files */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          {uploadingFiles.map((file) => (
            <Card key={file.id} className="overflow-hidden">
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-primary-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    {file.error ? (
                      <p className="text-xs text-destructive">{file.error}</p>
                    ) : (
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden mt-1">
                        <div
                          className="h-full bg-primary-500 transition-all duration-300"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                  {file.error && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeUploadingFile(file.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Uploaded files */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file) => (
            <Card key={file.publicId}>
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  {getFileIcon(file.type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{formatFileSize(file.size)}</span>
                      {file.extractedText ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-3 w-3" />
                          Procesado
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-600">
                          <AlertCircle className="h-3 w-3" />
                          Sin procesar
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(file.publicId)}
                    disabled={disabled}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* File count */}
      {files.length > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          {files.length} de {maxFiles} archivos
        </p>
      )}
    </div>
  );
}
